import { ReactNode } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { StatsCardMobile } from './StatsCardMobile'
import { StatsCardDesktop } from './StatsCardDesktop'
import { StatsCardBaseProps, MobileLayoutVariant } from './StatsCardBase'

interface StatsCardProps extends StatsCardBaseProps {
  chart?: ReactNode
  height?: string
  mobileLayout?: MobileLayoutVariant
  mobileHeight?: string
  mobileContentHeight?: string
}

/**
 * Unified StatsCard component that renders mobile or desktop version
 * Uses useIsMobile hook to conditionally render the appropriate version
 * This approach separates mobile and desktop code completely
 * 
 * @example
 * ```tsx
 * <StatsCard
 *   icon={inboxIcon}
 *   iconAlt="Total Projects"
 *   label="Total Projects"
 *   value={formatCompactNumber(totalProjects)}
 *   loading={loading}
 *   chart={<TotalProjectsGraph data={data} />}
 * />
 * ```
 */
export const StatsCard = ({
  mobileLayout,
  mobileHeight,
  mobileContentHeight,
  ...props
}: StatsCardProps) => {
  const isMobile = useIsMobile()

  // Completely separate mobile and desktop rendering
  // No CSS classes mixing - pure conditional rendering
  if (isMobile) {
    return (
      <StatsCardMobile 
        {...props}
        layout={mobileLayout}
        height={mobileHeight}
        contentHeight={mobileContentHeight}
      />
    )
  }

  return <StatsCardDesktop {...props} />
}

